import * as Sentry from "@sentry/react";
import type {
  ConfigurableSSEClient,
  ConfigurableSSEClientOptions,
  TopicSubscriptionEntry,
  SubscriptionHandle,
  SubscribeTopicParams,
  ConnectionStatus,
  StreamConfigPayload,
} from "./types";
import { PollingFallbackManager } from "./PollingFallbackManager";
import { ConnectionManager } from "./ConnectionManager";
import { getStreamNetworkDiagnostics } from "./networkDiagnostics";
import { diagnosticEventLog } from "@/shared/services/diagnostic/DiagnosticEventLog";
import type { DiagnosticSource } from "@/shared/services/diagnostic/types";

type Listener<T> = (data: T) => void;
const DEFAULT_RECONNECT_DELAY = 3000;

/**
 * A ConfigurableSSEClient that manages a shared SSE connection
 * with support for multiple widget subscriptions and event routing.
 *
 * Delegates connection management (including retry and activity monitoring)
 * to ConnectionManager, and polling fallback to PollingFallbackManager.
 */
export class ConfigurableSSEClientImpl implements ConfigurableSSEClient {
  // ===========================================
  // Private State
  // ===========================================

  private readonly config: ConfigurableSSEClientOptions;

  /** Registry for topic subscriptions (keyed by topic) */
  private topicRegistry: Map<string, TopicSubscriptionEntry> = new Map();

  /** Listeners for connection status changes */
  private statusListeners: Set<Listener<ConnectionStatus>> = new Set();

  /** Listeners for polling mode changes */
  private pollingModeListeners: Set<Listener<boolean>> = new Set();

  /** Pending cleanup (only one can exist per topic - scheduled when last subscriber removed) */
  private pendingCleanup: {
    topic: string;
    timerId: ReturnType<typeof setTimeout>;
  } | null = null;

  /** Timer for periodic SSE recovery attempts while in polling mode */
  private sseRecoveryTimer: ReturnType<typeof setInterval> | undefined;

  /** Timer for the Phase 1 SSE recovery probe check (30s — did it connect?) */
  private sseRecoveryProbeTimer: ReturnType<typeof setTimeout> | undefined;

  /** Timer for the Phase 2 SSE recovery probe check (+90s — is data flowing?) */
  private sseRecoveryProbePhase2Timer: ReturnType<typeof setTimeout> | undefined;

  /** How often to attempt SSE recovery while in polling mode (5 minutes) */
  private readonly sseRecoveryIntervalMs = 300000;

  /** Consecutive failed SSE recovery attempts in the current polling-mode run */
  private sseRecoveryFailureCount = 0;

  /** Whether we've already captured a "stuck in polling" Sentry message for the current run */
  private pollingStuckReported = false;

  /** Tracks when we last entered polling mode — used to detect false-success pattern */
  private lastPollingEntryTime = 0;

  /** How many times recovery "succeeded" but we re-entered polling soon after */
  private falseSuccessCount = 0;

  /** Set to true when routeEvent fires during a Phase 2 recovery probe */
  private sseRecoveryProbeActivityReceived = false;

  /** Timer for proactive periodic reconnects (belt-and-braces silent-disconnect defence) */
  private periodicReconnectTimer: ReturnType<typeof setInterval> | undefined;

  /** Guards against overlapping periodic reconnects if one is still in flight when the next fires */
  private periodicReconnectInFlight = false;

  private readonly logger: (...args: unknown[]) => void;
  private readonly cleanupDelayMs: number;
  private readonly diagnosticSource: DiagnosticSource;

  // ===========================================
  // Composed Subsystems
  // ===========================================

  private readonly pollingManager: PollingFallbackManager;
  private readonly connectionManager: ConnectionManager;

  // ===========================================
  // Constructor
  // ===========================================

  constructor(config: ConfigurableSSEClientOptions) {
    this.config = config;
    this.logger = config.logger ?? (() => {});
    this.cleanupDelayMs = config.cleanupDelayMs ?? 3000;
    this.diagnosticSource = `${(config.connectionName ?? "").toLowerCase()}:sse`;

    const connectionName = config.connectionName ?? "";

    // Initialize polling fallback manager
    this.pollingManager = new PollingFallbackManager({
      connectionName,
      pollingConfigs: config.pollingConfigs ?? [],
      logger: this.logger,
      onEvent: (topic, data) => this.dispatchToTopic(topic, data),
    });

    // Initialize connection manager (owns retry + activity monitoring internally)
    this.connectionManager = new ConnectionManager({
      url: config.url,
      connectionName,
      configureFn: config.configureFn,
      connectionAttemptTimeoutMs: config.connectionAttemptTimeoutMs ?? 15000,
      retryEnabled: config.retryEnabled ?? true,
      fallbackEnabled: (config.pollingConfigs ?? []).length > 0,
      logger: this.logger,

      // Retry + activity config
      reconnectDelay: config.reconnectDelay ?? DEFAULT_RECONNECT_DELAY,
      fallbackThreshold: config.fallbackThreshold ?? 5,
      sseStabilityWindowMs: config.sseStabilityWindowMs ?? 120000,
      activityTimeoutMs: config.activityTimeoutMs ?? 90000,

      // Callbacks
      onStatusChange: (status) => this.notifyStatusListeners(status),
      onEvent: (route, data) => this.routeEvent(route, data),
      onFallbackThresholdReached: () => this.enterPollingMode(),
      onCredentialsReceived: () =>
        this.connectionManager.reconfigure(this.buildConfigurePayloads()),

      // Guards
      hasSubscribers: () => this.hasAnySubscribers(),
    });

    // Start the proactive periodic reconnect timer if configured
    if (config.periodicReconnectMs && config.periodicReconnectMs > 0) {
      this.periodicReconnectTimer = setInterval(() => {
        void this.performPeriodicReconnect();
      }, config.periodicReconnectMs);
    }
  }

  // ===========================================
  // Public API - Subscribe Methods
  // ===========================================

  /**
   * Subscribe to a topic without filters.
   * Multiple subscribers to the same topic share a single server subscription.
   * Events are routed to all subscribers when the server sends topic:action messages.
   */
  subscribeTopic<T>(params: SubscribeTopicParams<T>): SubscriptionHandle {
    const { topic, callback } = params;

    this.logger(
      `[${this.config.connectionName}] subscribeTopic called for ${topic}, existing callbacks: ${
        this.topicRegistry.get(topic)?.callbacks.size ?? 0
      }`
    );

    // Cancel pending cleanup if it's for this topic (React re-subscribe scenario)
    if (this.pendingCleanup?.topic === topic) {
      this.logger(
        `[${this.config.connectionName}] Cancelling pending cleanup for topic ${topic}`
      );
      clearTimeout(this.pendingCleanup.timerId);
      this.pendingCleanup = null;
    }

    // Handle topic-only subscription
    const existingEntry = this.topicRegistry.get(topic);
    // Only reconfigure if this is a truly new topic (no entry at all)
    // If entry exists but has 0 callbacks, it's a React re-subscribe - don't reconfigure
    const needsReconfigure = !existingEntry;

    if (existingEntry) {
      // Topic entry exists - add the callback (handles both active and pending-cleanup entries)
      existingEntry.callbacks.add(callback as Listener<unknown>);
    } else {
      // First subscription for this topic - create new entry
      const entry: TopicSubscriptionEntry = {
        callbacks: new Set([callback as Listener<unknown>]),
      };
      this.topicRegistry.set(topic, entry);
    }

    diagnosticEventLog.record(this.diagnosticSource, "info", "topic-subscribe", {
      topic,
      isNew: needsReconfigure,
      totalTopics: this.topicRegistry.size,
    });

    // In polling mode, start polling for this topic if config exists
    if (this.pollingManager.isPollingMode) {
      const pollingConfig = this.pollingManager.pollingConfigMap.get(topic);
      if (pollingConfig) {
        this.pollingManager.startTopicPolling(topic, pollingConfig);
      }
    } else {
      // Determine if connection action is needed
      if (!this.isConnectedOrPending()) {
        this.connectionManager.connect();
      } else if (needsReconfigure && this.isConnected()) {
        // Reconfigure when adding new topic
        this.logger(
          `[${this.config.connectionName}] New topic subscription, reconfiguring...`
        );
        this.connectionManager.reconfigure(this.buildConfigurePayloads());
      }
    }

    // Return unsubscribe handle
    const callbackRef = callback as Listener<unknown>;
    return {
      unsubscribe: () => this.unsubscribeTopicCallback(topic, callbackRef),
    };
  }

  /**
   * Subscribe to connection status changes (no payload/auth needed).
   */
  onStatusChange(callback: Listener<ConnectionStatus>): SubscriptionHandle {
    this.statusListeners.add(callback);

    // Immediately notify with current status
    setTimeout(() => callback(this.connectionManager.status), 0);

    return {
      unsubscribe: () => {
        this.statusListeners.delete(callback);
      },
    };
  }

  /**
   * Subscribe to polling mode changes.
   */
  onPollingModeChange(callback: Listener<boolean>): SubscriptionHandle {
    this.pollingModeListeners.add(callback);

    // Immediately notify with current state
    setTimeout(() => callback(this.pollingManager.isPollingMode), 0);

    return {
      unsubscribe: () => {
        this.pollingModeListeners.delete(callback);
      },
    };
  }

  // ===========================================
  // Private - Unsubscribe Methods
  // ===========================================

  /**
   * Unsubscribe a callback from a topic subscription.
   * Immediately removes the callback. Delays registry deletion only if this would close the connection.
   */
  private unsubscribeTopicCallback(
    topic: string,
    callback: Listener<unknown>
  ): void {
    const entry = this.topicRegistry.get(topic);
    if (!entry) {
      this.logger(
        `[${this.config.connectionName}] Unsubscribe: no topic entry for ${topic}`
      );
      return;
    }

    this.logger(
      `[${this.config.connectionName}] Unsubscribing callback from topic ${topic}, callbacks before: ${entry.callbacks.size}`
    );

    // Immediately remove this specific callback (so it won't receive events)
    entry.callbacks.delete(callback);

    diagnosticEventLog.record(this.diagnosticSource, "info", "topic-unsubscribe", {
      topic,
      remainingCallbacks: entry.callbacks.size,
      totalTopics: this.topicRegistry.size,
    });

    this.logger(
      `[${this.config.connectionName}] Callbacks after delete: ${entry.callbacks.size}`
    );

    // If no callbacks remain for this topic
    if (entry.callbacks.size === 0) {
      // In polling mode, stop polling for this topic
      if (this.pollingManager.isPollingMode) {
        this.pollingManager.stopTopicPolling(topic);
      }

      // Check if other topics still have active subscribers
      const otherSubscribersExist = Array.from(
        this.topicRegistry.values()
      ).some((e) => e.callbacks.size > 0);

      if (!otherSubscribersExist) {
        // Last subscription - delay both registry deletion AND connection close
        // Keep entry in registry so re-subscribe can reuse it
        this.scheduleCleanup(topic);
      } else {
        // Other subscribers exist - delete immediately and reconfigure server
        this.topicRegistry.delete(topic);
        if (!this.pollingManager.isPollingMode) {
          this.logger(
            `[${this.config.connectionName}] Other subscribers remain, reconfiguring...`
          );
          this.connectionManager.reconfigure(this.buildConfigurePayloads());
        }
      }
    }
  }

  // ===========================================
  // Private - Cleanup & Scheduling
  // ===========================================

  /**
   * Schedule registry deletion and connection close with delay.
   * Used when the last subscription is removed to handle React effect cleanup/re-subscribe.
   * If a new subscribe happens before the timer fires, the timer is cancelled and
   * the existing registry entry is reused.
   */
  private scheduleCleanup(topic: string): void {
    this.logger(
      `[${this.config.connectionName}] Last subscription removed (topic: ${topic}), scheduling cleanup (${this.cleanupDelayMs}ms)`
    );

    const timerId = setTimeout(() => {
      this.pendingCleanup = null;

      this.topicRegistry.delete(topic);

      this.logger(
        `[${this.config.connectionName}] Cleanup timer fired for topic: ${topic}`
      );

      // Re-check if still no subscribers (a new subscribe might have happened)
      if (!this.hasAnySubscribers()) {
        this.logger(
          `[${this.config.connectionName}] No subscribers after delay, clearing server subscriptions and closing connection`
        );
        // Send configure request with empty configs to tell server to clear all subscriptions
        this.connectionManager
          .reconfigure(this.buildConfigurePayloads())
          .then(() => {
            this.destroy();
          })
          .catch((error) => {
            this.logger(
              `[${this.config.connectionName}] Failed to clear subscriptions, closing anyway:`,
              error
            );
            this.destroy();
          });
      } else {
        this.logger(
          `[${this.config.connectionName}] New subscribers added during delay, keeping connection`
        );
      }
    }, this.cleanupDelayMs);

    this.pendingCleanup = { topic, timerId };
  }

  // ===========================================
  // Public API - Connection Control
  // ===========================================

  /**
   * Returns whether the underlying SSE connection is currently open.
   */
  isConnected(): boolean {
    return this.connectionManager.status === "active";
  }

  /**
   * Full teardown - clears all timers, closes connection, and resets state.
   */
  destroy(): void {
    if (this.pendingCleanup) {
      clearTimeout(this.pendingCleanup.timerId);
      this.pendingCleanup = null;
    }

    clearInterval(this.sseRecoveryTimer);
    this.sseRecoveryTimer = undefined;

    clearTimeout(this.sseRecoveryProbeTimer);
    this.sseRecoveryProbeTimer = undefined;

    clearTimeout(this.sseRecoveryProbePhase2Timer);
    this.sseRecoveryProbePhase2Timer = undefined;

    clearInterval(this.periodicReconnectTimer);
    this.periodicReconnectTimer = undefined;

    this.connectionManager.destroy();
    this.pollingManager.destroy();
  }

  // ===========================================
  // Private - Connection Orchestration
  // ===========================================

  /**
   * Switch to polling mode. Tears down the SSE connection and starts
   * polling for all currently subscribed topics.
   */
  private enterPollingMode(): void {
    diagnosticEventLog.record(this.diagnosticSource, "warn", "entering-polling-mode");
    this.connectionManager.cancelRetry();
    this.connectionManager.disconnect();
    this.connectionManager.setStatus("active");
    this.pollingManager.activate();

    // Detect false-success pattern: if we re-entered polling within 10 minutes
    // of leaving it, the previous recovery was a false success.
    const now = Date.now();
    if (this.lastPollingEntryTime > 0 && now - this.lastPollingEntryTime < 600000) {
      this.falseSuccessCount++;
      this.logger(
        `[${this.config.connectionName}] False success pattern detected (count: ${this.falseSuccessCount})`
      );
      diagnosticEventLog.record(this.diagnosticSource, "warn", "false-success-detected", {
        falseSuccessCount: this.falseSuccessCount,
      });
    } else {
      // Genuine new polling entry — reset counters
      this.sseRecoveryFailureCount = 0;
      this.pollingStuckReported = false;
    }
    this.lastPollingEntryTime = now;

    // Breadcrumb + persistent session tag instead of captureMessage.
    // Transient drops that recover within 5 min generate zero issues.
    // Every other error the user hits while in polling will be tagged so you
    // can filter Sentry by `sse.<connection>:polling` to see the real impact,
    // and a separate captureMessage fires if they stay stuck (see recovery timer).
    const networkDiagnostics = getStreamNetworkDiagnostics(
      this.config.connectionName ?? "",
      this.config.url
    );
    Sentry.addBreadcrumb({
      category: "sse",
      message: `[${this.config.connectionName}] Entered polling fallback mode`,
      level: "warning",
      data: {
        ...this.connectionManager.getDiagnosticState(),
        fallbackThreshold: this.config.fallbackThreshold ?? 5,
        activityTimeoutMs: this.config.activityTimeoutMs ?? 90000,
        network: networkDiagnostics,
      },
    });
    Sentry.setTag(`sse.${this.config.connectionName ?? "unknown"}`, "polling");

    this.notifyPollingModeChange(true);
    this.startPolling();
    this.startSSERecoveryTimer();
  }

  /**
   * Start polling for each subscribed topic that has a polling config.
   */
  private startPolling(): void {
    for (const [topic, entry] of this.topicRegistry) {
      if (entry.callbacks.size > 0) {
        const pollingConfig = this.pollingManager.pollingConfigMap.get(topic);
        if (pollingConfig) {
          this.pollingManager.startTopicPolling(topic, pollingConfig);
        }
      }
    }
  }

  /**
   * Periodically attempt to reconnect via SSE while in polling mode.
   *
   * Uses a two-phase probe to avoid false successes:
   *   Phase 1 (30s) — Did the connection reach "active" status?
   *   Phase 2 (+90s) — Is real message/ping activity flowing?
   *
   * Polling continues uninterrupted during both phases so users never
   * lose data. Only exits polling when Phase 2 confirms real activity.
   */
  private startSSERecoveryTimer(): void {
    clearInterval(this.sseRecoveryTimer);

    // Double the interval after false successes to avoid rapid cycling
    const effectiveInterval =
      this.falseSuccessCount > 0
        ? this.sseRecoveryIntervalMs * 2
        : this.sseRecoveryIntervalMs;

    this.sseRecoveryTimer = setInterval(() => {
      if (!this.pollingManager.isPollingMode || !this.hasAnySubscribers()) {
        return;
      }

      this.logger(
        `[${this.config.connectionName}] Attempting SSE recovery from polling mode...`
      );
      diagnosticEventLog.record(this.diagnosticSource, "info", "sse-recovery-attempt", {
        falseSuccessCount: this.falseSuccessCount,
        effectiveInterval,
      });

      this.sseRecoveryProbeActivityReceived = false;
      this.connectionManager.connect();

      // Phase 1: 30s — did the connection establish at all?
      this.sseRecoveryProbeTimer = setTimeout(() => {
        if (this.connectionManager.status !== "active") {
          // Real failure — SSE couldn't even connect
          this.logger(
            `[${this.config.connectionName}] SSE recovery Phase 1 failed (status: ${this.connectionManager.status}), staying in polling mode`
          );
          this.sseRecoveryFailureCount += 1;
          diagnosticEventLog.record(this.diagnosticSource, "info", "sse-recovery-failed", {
            phase: 1,
            failureCount: this.sseRecoveryFailureCount,
          });
          this.connectionManager.disconnect();
          this.reportStuckInPollingIfNeeded();
          return;
        }

        // Phase 2: additional 90s — is real data actually flowing?
        // Polling continues in parallel during this window.
        this.logger(
          `[${this.config.connectionName}] SSE recovery Phase 1 passed (status=active), entering Phase 2 stability check...`
        );
        diagnosticEventLog.record(this.diagnosticSource, "info", "sse-recovery-phase2-start");

        this.sseRecoveryProbePhase2Timer = setTimeout(() => {
          if (
            this.sseRecoveryProbeActivityReceived &&
            this.connectionManager.status === "active"
          ) {
            // Genuine success — real data flowing
            this.logger(
              `[${this.config.connectionName}] SSE recovery confirmed — real activity received during probe`
            );
            diagnosticEventLog.record(this.diagnosticSource, "info", "sse-recovery-success", {
              phase: 2,
            });
            this.pollingManager.deactivate();
            this.notifyPollingModeChange(false);
            this.falseSuccessCount = 0;
            this.sseRecoveryFailureCount = 0;
            this.pollingStuckReported = false;
            this.lastPollingEntryTime = 0;
            Sentry.setTag(`sse.${this.config.connectionName ?? "unknown"}`, "sse");
            clearInterval(this.sseRecoveryTimer);
            this.sseRecoveryTimer = undefined;
          } else {
            // False success — connected but no real data
            this.logger(
              `[${this.config.connectionName}] SSE recovery false success — active but no activity received`
            );
            this.sseRecoveryFailureCount += 1;
            diagnosticEventLog.record(this.diagnosticSource, "warn", "sse-recovery-false-success", {
              failureCount: this.sseRecoveryFailureCount,
              falseSuccessCount: this.falseSuccessCount,
              activityReceived: this.sseRecoveryProbeActivityReceived,
              status: this.connectionManager.status,
            });
            this.connectionManager.disconnect();
            this.reportStuckInPollingIfNeeded();
          }
        }, 90000);
      }, 30000);
    }, effectiveInterval);
  }

  /**
   * Fire a single Sentry message when stuck in polling for too long.
   * Only once per polling-mode run — cleared on successful recovery.
   */
  private reportStuckInPollingIfNeeded(): void {
    if (this.sseRecoveryFailureCount < 3 || this.pollingStuckReported) {
      return;
    }

    this.pollingStuckReported = true;
    const stuckMinutes = Math.round(
      (this.sseRecoveryFailureCount * this.sseRecoveryIntervalMs) / 60000
    );
    const network = getStreamNetworkDiagnostics(
      this.config.connectionName ?? "",
      this.config.url
    );
    Sentry.withScope((scope) => {
      scope.setLevel("warning");
      scope.setTag("sse.connection", this.config.connectionName ?? "");
      scope.setTag("sse.online", String(network.online));
      if (network.connectionType) {
        scope.setTag("sse.connection_type", network.connectionType);
      }
      if (network.resourceTiming?.nextHopProtocol) {
        scope.setTag(
          "sse.http_protocol",
          network.resourceTiming.nextHopProtocol
        );
      }
      if (
        network.vendorProxyHeaders &&
        Object.keys(network.vendorProxyHeaders).length > 0
      ) {
        scope.setTag("sse.vendor_proxy_detected", "true");
      }
      if (network.responseHeaders?.via) {
        scope.setTag("sse.via_header", "true");
      }
      scope.setContext("sse_stuck", {
        ...this.connectionManager.getDiagnosticState(),
        failedRecoveryAttempts: this.sseRecoveryFailureCount,
        falseSuccessCount: this.falseSuccessCount,
        recoveryProbeActivityReceived: this.sseRecoveryProbeActivityReceived,
        approxMinutesStuck: stuckMinutes,
        network,
      });
      Sentry.captureMessage(
        `SSE stuck in polling mode — ${this.sseRecoveryFailureCount} failed recovery attempts (~${stuckMinutes} min)`
      );
    });
  }

  // ===========================================
  // Private - Proactive Periodic Reconnect
  // ===========================================

  /**
   * Close and reopen the SSE connection as a belt-and-braces defence against
   * silent disconnects (corporate proxies, NAT timeouts, SSL inspection buffering).
   * Runs once per `periodicReconnectMs` tick. After the new connection stabilises,
   * invokes any configured per-topic catch-up functions and dispatches their results
   * through the normal topic delivery path so existing dedup handlers collapse any
   * overlap with live SSE messages.
   *
   * Skipped if: the connection isn't currently active, we're in polling fallback
   * mode, there are no subscribers, or a prior cycle is still in flight.
   */
  private async performPeriodicReconnect(): Promise<void> {
    if (this.periodicReconnectInFlight) {
      return;
    }

    if (
      this.connectionManager.status !== "active" ||
      this.pollingManager.isPollingMode ||
      !this.hasAnySubscribers()
    ) {
      diagnosticEventLog.record(
        this.diagnosticSource,
        "info",
        "periodic-reconnect-skipped",
        {
          status: this.connectionManager.status,
          isPollingMode: this.pollingManager.isPollingMode,
          hasSubscribers: this.hasAnySubscribers(),
        }
      );
      return;
    }

    this.periodicReconnectInFlight = true;

    const startedAt = Date.now();
    diagnosticEventLog.record(
      this.diagnosticSource,
      "info",
      "periodic-reconnect-start",
      { periodicReconnectMs: this.config.periodicReconnectMs }
    );
    Sentry.addBreadcrumb({
      category: "sse",
      level: "info",
      message: `[${this.config.connectionName}] Proactive periodic reconnect`,
      data: { periodicReconnectMs: this.config.periodicReconnectMs },
    });
    this.logger(
      `[${this.config.connectionName}] Proactive periodic reconnect starting`
    );

    try {
      await this.reconnectAndWaitForActive(15000);

      // Connection is back up — fire per-topic catch-up fetches to close the gap
      await this.runCatchUpFns();

      diagnosticEventLog.record(
        this.diagnosticSource,
        "info",
        "periodic-reconnect-success",
        { elapsedMs: Date.now() - startedAt }
      );
    } catch (error) {
      this.logger(
        `[${this.config.connectionName}] Proactive periodic reconnect did not complete cleanly:`,
        error
      );
      diagnosticEventLog.record(
        this.diagnosticSource,
        "warn",
        "periodic-reconnect-failed",
        {
          elapsedMs: Date.now() - startedAt,
          error: error instanceof Error ? error.message : String(error),
        }
      );
      // Don't surface to Sentry as an error — the existing retry/fallback
      // machinery will recover. We'll pick up again on the next tick.
    } finally {
      this.periodicReconnectInFlight = false;
    }
  }

  /**
   * Trigger a connection cycle and resolve when the connection transitions
   * back to "active" (credentials received + configure succeeded), or reject
   * after `timeoutMs` if it never gets there.
   */
  private reconnectAndWaitForActive(timeoutMs: number): Promise<void> {
    return new Promise((resolve, reject) => {
      // connect() synchronously transitions status to "pending", so any status
      // listener registered afterwards will first be replayed with "pending"
      // and then fire again with "active" once the new connection stabilises.
      this.connectionManager.connect();

      const timeoutId = setTimeout(() => {
        handle.unsubscribe();
        reject(
          new Error(
            `Timed out waiting for active status after ${timeoutMs}ms`
          )
        );
      }, timeoutMs);

      const handle = this.onStatusChange((status) => {
        if (status === "active") {
          clearTimeout(timeoutId);
          handle.unsubscribe();
          resolve();
        }
      });
    });
  }

  /**
   * Invoke every configured catch-up function in parallel, dispatching each
   * returned item through `dispatchToTopic` so topic subscribers receive them
   * via their normal callback path. Failures are logged but do not throw —
   * a broken catch-up fetch is non-fatal; the SSE stream is already live.
   */
  private async runCatchUpFns(): Promise<void> {
    const catchUpFns = this.config.catchUpFns;
    if (!catchUpFns) return;

    const entries = Object.entries(catchUpFns);
    if (entries.length === 0) return;

    await Promise.all(
      entries.map(async ([topic, fn]) => {
        try {
          const items = await fn();
          diagnosticEventLog.record(
            this.diagnosticSource,
            "info",
            "periodic-reconnect-catchup",
            { topic, itemCount: items.length }
          );
          this.logger(
            `[${this.config.connectionName}] Catch-up fetched ${items.length} items for topic "${topic}"`
          );
          for (const item of items) {
            this.dispatchToTopic(topic, item);
          }
        } catch (error) {
          this.logger(
            `[${this.config.connectionName}] Catch-up fetch failed for topic "${topic}":`,
            error
          );
          diagnosticEventLog.record(
            this.diagnosticSource,
            "warn",
            "periodic-reconnect-catchup-failed",
            {
              topic,
              error: error instanceof Error ? error.message : String(error),
            }
          );
        }
      })
    );
  }

  // ===========================================
  // Private - Event Routing
  // ===========================================

  /**
   * Route an SSE event (topic:action format) to the appropriate topic callbacks.
   */
  private routeEvent(route: string, data: unknown): void {
    // Signal Phase 2 recovery probe that real SSE activity is flowing
    this.sseRecoveryProbeActivityReceived = true;
    const parts = route.split(":");

    if (parts.length < 2) {
      this.logger(
        `[${this.config.connectionName}] Invalid event type format: ${route}`
      );
      return;
    }

    this.dispatchToTopic(parts[0], data);
  }

  /**
   * Dispatch data to all callbacks registered for a topic.
   * Shared delivery path for both SSE events and polled items.
   */
  private dispatchToTopic(topic: string, data: unknown): void {
    const topicEntry = this.topicRegistry.get(topic);
    if (topicEntry) {
      topicEntry.callbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          this.logger(
            `[${this.config.connectionName}] Error in topic subscription callback`,
            error
          );
          Sentry.withScope((scope) => {
            scope.setTag("sse.connection", this.config.connectionName ?? "");
            scope.setTag("sse.topic", topic);
            Sentry.captureException(
              new Error("Error in topic subscription callback", {
                cause: error as Error,
              })
            );
          });
        }
      });
    }
  }

  // ===========================================
  // Private - Status Management
  // ===========================================

  private notifyPollingModeChange(isPolling: boolean): void {
    this.pollingModeListeners.forEach((callback) => {
      try {
        callback(isPolling);
      } catch (error) {
        this.logger(
          `[${this.config.connectionName}] Error in polling mode change callback`,
          error
        );
        Sentry.withScope((scope) => {
          scope.setTag("sse.connection", this.config.connectionName ?? "");
          Sentry.captureException(
            new Error("Error in polling mode change callback", {
              cause: error as Error,
            })
          );
        });
      }
    });
  }

  private notifyStatusListeners(newStatus: ConnectionStatus): void {
    this.statusListeners.forEach((callback) => {
      try {
        callback(newStatus);
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setTag("sse.connection", this.config.connectionName ?? "");
          Sentry.captureException(
            new Error("Error in status change callback", {
              cause: error as Error,
            })
          );
        });
      }
    });
  }

  // ===========================================
  // Private - Utility Methods
  // ===========================================

  private hasAnySubscribers(): boolean {
    return this.topicRegistry.size > 0;
  }

  private isConnectedOrPending(): boolean {
    return (
      this.connectionManager.status === "active" ||
      this.connectionManager.status === "pending"
    );
  }

  /**
   * Build configuration payloads from current topic subscriptions.
   */
  private buildConfigurePayloads(): StreamConfigPayload[] {
    const configs: StreamConfigPayload[] = [];

    this.topicRegistry.forEach((entry, topic) => {
      if (entry.callbacks.size > 0) {
        configs.push({ topic });
      }
    });

    return configs;
  }
}
